import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  adminListProjects,
  adminSaveProject,
  adminUpdateProjectStatus,
  adminDeleteProject,
  adminListContactsLite,
  adminListEstimatesLite,
} from "@/lib/projects.functions";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Calendar, User, MapPin, Phone, Mail, GripVertical } from "lucide-react";
import { ClientPicker } from "@/components/ClientPicker";
import { ProjectPunchlist } from "@/components/ProjectPunchlist";
import { ProjectParts } from "@/components/ProjectParts";

export const Route = createFileRoute("/tools/projects")({
  head: () => ({
    meta: [
      { title: "Project Tracker | Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ProjectsBoard,
});

type Project = Awaited<ReturnType<typeof adminListProjects>>[number];
type Status = "lead" | "scheduled" | "in_progress" | "complete";
type Contact = Awaited<ReturnType<typeof adminListContactsLite>>[number];
type Estimate = Awaited<ReturnType<typeof adminListEstimatesLite>>[number];

const COLUMNS: { key: Status; label: string; accent: string }[] = [
  { key: "lead", label: "Lead", accent: "bg-slate-500" },
  { key: "scheduled", label: "Scheduled", accent: "bg-blue-500" },
  { key: "in_progress", label: "In Progress", accent: "bg-amber-500" },
  { key: "complete", label: "Complete", accent: "bg-emerald-500" },
];

const emptyForm = {
  id: undefined as string | undefined,
  title: "",
  status: "lead" as Status,
  client_id: null as string | null,
  client_name: "",
  client_email: "",
  client_phone: "",
  client_address: "",
  notes: "",
  start_date: "",
  end_date: "",
  contact_submission_id: null as string | null,
  saved_estimate_id: null as string | null,
};

function fmtDateRange(start: string | null, end: string | null) {
  if (!start && !end) return null;
  if (start && end && start !== end) return `${start} → ${end}`;
  return start || end;
}

function ProjectsBoard() {
  const [rows, setRows] = useState<Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [estimates, setEstimates] = useState<Estimate[]>([]);

  async function load() {
    try {
      setRows(await adminListProjects());
    } catch (e: any) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
    adminListContactsLite().then(setContacts).catch(() => {});
    adminListEstimatesLite().then(setEstimates).catch(() => {});
  }, []);

  const byStatus = useMemo(() => {
    const map: Record<Status, Project[]> = {
      lead: [],
      scheduled: [],
      in_progress: [],
      complete: [],
    };
    (rows ?? []).forEach((p) => map[p.status as Status]?.push(p));
    return map;
  }, [rows]);

  function openNew(status: Status = "lead") {
    setForm({ ...emptyForm, status });
    setDialogOpen(true);
  }

  function openEdit(p: Project) {
    setForm({
      id: p.id,
      title: p.title,
      status: p.status as Status,
      client_id: p.client_id ?? null,
      client_name: p.client_name ?? "",
      client_email: p.client_email ?? "",
      client_phone: p.client_phone ?? "",
      client_address: p.client_address ?? "",
      notes: p.notes ?? "",
      start_date: p.start_date ?? "",
      end_date: p.end_date ?? "",
      contact_submission_id: p.contact_submission_id ?? null,
      saved_estimate_id: p.saved_estimate_id ?? null,
    });
    setDialogOpen(true);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await adminSaveProject({ data: form as any });
      setDialogOpen(false);
      setForm(emptyForm);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string, title: string) {
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await adminDeleteProject({ data: { id } });
    load();
  }

  async function moveTo(id: string, status: Status) {
    // Optimistic update
    setRows((r) => r?.map((p) => (p.id === id ? { ...p, status } : p)) ?? null);
    try {
      await adminUpdateProjectStatus({ data: { id, status } });
    } catch (e: any) {
      setError(e.message);
      load();
    }
  }

  function pickContact(id: string) {
    const c = contacts.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({
      ...f,
      title: f.title || `${c.name} — inquiry`,
      client_name: f.client_name || c.name,
      client_email: f.client_email || c.email,
      client_phone: f.client_phone || c.phone || "",
      client_address: f.client_address || c.city || "",
      notes: f.notes || c.message,
      contact_submission_id: id,
    }));
  }

  function pickEstimate(id: string) {
    const e = estimates.find((x) => x.id === id);
    if (!e) return;
    setForm((f) => ({
      ...f,
      title: f.title || e.name,
      client_name: f.client_name || e.client_name || "",
      client_email: f.client_email || e.client_email || "",
      client_phone: f.client_phone || e.client_phone || "",
      client_address: f.client_address || e.client_address || "",
      saved_estimate_id: id,
    }));
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Project tracker</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Drag cards between columns to update status. Day-before email reminders send automatically.
          </p>
        </div>
        <Button onClick={() => openNew()}>
          <Plus className="mr-2 h-4 w-4" /> New project
        </Button>
      </div>

      {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {COLUMNS.map((col) => (
          <div
            key={col.key}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
            }}
            onDrop={(e) => {
              e.preventDefault();
              const id = e.dataTransfer.getData("text/plain");
              if (id) moveTo(id, col.key);
              setDragging(null);
            }}
            className={`flex min-h-[300px] flex-col rounded-lg border border-border bg-muted/20 p-3 ${
              dragging ? "transition-colors" : ""
            }`}
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${col.accent}`} />
                <h2 className="font-display text-sm font-semibold uppercase tracking-wide">
                  {col.label}
                </h2>
                <span className="text-xs text-muted-foreground">
                  {byStatus[col.key].length}
                </span>
              </div>
              <button
                onClick={() => openNew(col.key)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={`New ${col.label}`}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-1 flex-col gap-2">
              {!rows ? (
                <p className="text-xs text-muted-foreground">Loading…</p>
              ) : byStatus[col.key].length === 0 ? (
                <p className="text-xs text-muted-foreground">Drop cards here</p>
              ) : (
                byStatus[col.key].map((p) => {
                  const range = fmtDateRange(p.start_date, p.end_date);
                  return (
                    <div
                      key={p.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", p.id);
                        e.dataTransfer.effectAllowed = "move";
                        setDragging(p.id);
                      }}
                      onDragEnd={() => setDragging(null)}
                      onClick={() => openEdit(p)}
                      className={`group cursor-pointer rounded-md border border-border bg-card p-3 shadow-sm transition hover:border-foreground/30 hover:shadow ${
                        dragging === p.id ? "opacity-50" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="line-clamp-2 text-sm font-semibold">{p.title}</h3>
                        <GripVertical className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
                      </div>
                      {p.client_name && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <User className="h-3 w-3" /> {p.client_name}
                        </div>
                      )}
                      {p.client_address && (
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" />{" "}
                          <span className="line-clamp-1">{p.client_address}</span>
                        </div>
                      )}
                      {range && (
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" /> {range}
                        </div>
                      )}
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          {p.client_phone && (
                            <a
                              href={`tel:${p.client_phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="hover:text-foreground"
                            >
                              <Phone className="h-3.5 w-3.5" />
                            </a>
                          )}
                          {p.client_email && (
                            <a
                              href={`mailto:${p.client_email}`}
                              onClick={(e) => e.stopPropagation()}
                              className="hover:text-foreground"
                            >
                              <Mail className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(p.id, p.title);
                          }}
                          className="text-muted-foreground opacity-0 transition hover:text-destructive group-hover:opacity-100"
                          aria-label="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit project" : "New project"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Smith home theater install"
              />
            </div>

            {!form.id && (contacts.length > 0 || estimates.length > 0) && (
              <div className="grid gap-3 rounded-md border border-dashed border-border p-3 sm:grid-cols-2">
                {contacts.length > 0 && (
                  <div>
                    <Label className="text-xs">Prefill from contact</Label>
                    <Select
                      value={form.contact_submission_id ?? ""}
                      onValueChange={(v) => v && pickContact(v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pick a contact…" />
                      </SelectTrigger>
                      <SelectContent>
                        {contacts.slice(0, 50).map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name} — {c.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {estimates.length > 0 && (
                  <div>
                    <Label className="text-xs">Prefill from estimate</Label>
                    <Select
                      value={form.saved_estimate_id ?? ""}
                      onValueChange={(v) => v && pickEstimate(v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pick an estimate…" />
                      </SelectTrigger>
                      <SelectContent>
                        {estimates.slice(0, 50).map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.name}
                            {e.client_name ? ` — ${e.client_name}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v as Status })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLUMNS.map((c) => (
                      <SelectItem key={c.key} value={c.key}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Start date</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End date</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <ClientPicker
              clientId={form.client_id}
              clientName={form.client_name}
              onPick={(f) =>
                setForm({
                  ...form,
                  client_id: f.client_id,
                  client_name: f.client_name ?? "",
                  client_email: f.client_email ?? "",
                  client_phone: f.client_phone ?? "",
                  client_address: f.client_address ?? "",
                })
              }
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Client name</Label>
                <Input
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Client phone</Label>
                <Input
                  value={form.client_phone}
                  onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Client email</Label>
                <Input
                  type="email"
                  value={form.client_email}
                  onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={form.client_address}
                  onChange={(e) => setForm({ ...form, client_address: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                rows={4}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Gate code, equipment list, crew instructions…"
              />
            </div>

            {form.id && <ProjectParts projectId={form.id} />}
            {form.id && <ProjectPunchlist projectId={form.id} />}
          </div>
          <DialogFooter>
            {form.id && (
              <Button
                variant="ghost"
                className="mr-auto text-destructive hover:text-destructive"
                onClick={() => {
                  remove(form.id!, form.title);
                  setDialogOpen(false);
                }}
              >
                <Trash2 className="mr-1 h-4 w-4" /> Delete
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving || !form.title.trim()}>
              {saving ? "Saving…" : form.id ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
